import type { FileUploadAndParseProps } from "@fronterahealth/frontera-ui-components";

import { useCreateTemplateLogo, useUpdateTemplateLogo } from "@api/rest/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";

export const useGetS3BucketTemplateLogoUrlRetriever = ({
  templateId,
}: {
  templateId: number;
}): FileUploadAndParseProps["S3BucketUrlRetriever"] => {
  const createFileMutation = useCreateTemplateLogo();

  return async ({ fileName, mimeType, fileSize }) => {
    let bucket = "<missing-bucket-url>";
    let fileId = "<missing-file-id>";

    try {
      const res = await createFileMutation.mutateAsync({
        data: {
          template_id: templateId,
          file_size: fileSize,
          mime_type: mimeType,
          original_file_name: fileName,
        },
      });

      if (res.status !== 200) {
        notifyError("Error creating logo upload URL");
        throw new Error("Error creating logo upload URL");
      }

      bucket = res.data.file_url;
      fileId = String(templateId);
      return { bucket, fileId };
    } catch (err) {
      console.error("Error retrieving S3 bucket URL:", err);
      throw new Error(String(err));
    }
  };
};

export const useGetS3BucketUploadTemplateLogoCallback = ({
  templateId,
  refetch,
}: {
  templateId: number;
  refetch: () => Promise<void> | unknown;
}): FileUploadAndParseProps["S3BucketUploadCallback"] => {
  const updateFileUrlMutation = useUpdateTemplateLogo();

  const uploadWithRetry = async ({
    file,
    mimeType,
    url,
    retries = 5,
    attempt = 1,
  }: {
    file: File;
    mimeType: string;
    url: string;
    retries?: number;
    attempt?: number;
  }): Promise<Response> => {
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": mimeType },
        body: file,
      });

      if (response.status === 200) return response;
      throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
    } catch (err) {
      if (attempt < retries) {
        const backoff = 100 * 2 ** (attempt - 1);
        await new Promise((resolve) => setTimeout(resolve, backoff));
        return uploadWithRetry({ file, mimeType, url, retries, attempt: attempt + 1 });
      }
      throw err;
    }
  };

  return async ({ file, mimeType, url }) => {
    try {
      await uploadWithRetry({ file, mimeType, url });

      const res = await updateFileUrlMutation.mutateAsync({
        data: { template_id: templateId, original_file_name: file.name },
      });

      if (res.status !== 200) {
        notifyError("Could not confirm logo upload.");
        return;
      }

      await refetch();
    } catch (err) {
      console.error("Error uploading logo to S3:", err);
      throw new Error(String(err));
    }
  };
};
